import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, LayoutGrid, Target, Upload, Search, Sparkles } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { candidateService } from '@core/supabase/services/candidateService';
import { jobService } from '@core/supabase/services/jobService';
import { clientService } from '@core/supabase/services/clientService';
import { applicationService } from '@core/supabase/services/applicationService';
import type { Candidate, Client, Job, JobStatus } from '@core/types';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { EmptyState } from '@shared/components/ui/EmptyState';
import { TablePagination } from '@shared/components/ui/TablePagination';
import { DataTableToolbar } from '@shared/components/ui/DataTableToolbar';
import { ListConfigureButton } from '@shared/components/ui/ListConfigureButton';
import { ListOperationsDropdown, type ListExportFmt } from '@shared/components/ui/ListOperationsDropdown';
import { objectHistoryUrl } from '@shared/lib/objectHistoryUrl';
import { ConfigureListColumnsModal, colClass } from '@shared/components/ui/ConfigureListColumnsModal';
import { useListColumnVisibility } from '@shared/hooks/useListColumnVisibility';
import type { ListColumnCatalogItem } from '@shared/lib/listColumnPreferences';
import { listExportNotImplemented } from '@shared/lib/listExportStub';
import { computeRoleFitPair, type RoleFitRow } from './computePairFit';
import { candidatePortraitUrl } from './candidatePortraitUrl';
import { RoleFitBulkImportTab } from './RoleFitBulkImportTab';
import { RoleFitSourcingTab } from './RoleFitSourcingTab';
import { SmartMatchRedirectCTA } from '@modules/smart-match/SmartMatchRedirectCTA';
import { buildRoleFitMatrixCsv } from './RoleFitMatrixToolbar';
import { SortableTh } from '@shared/components/ui/SortableTableHeader';
import { formatCreatedWhen } from '@shared/lib/formatCreatedWhen';
import { GenericManageRightsModal } from '@shared/components/list/GenericManageRightsModal';
import { ConfirmDialog } from '@shared/components/ui/ConfirmDialog';

const MAX_PAIRS = 3500;
const CANDIDATE_PAGE_SIZE = 400;
const JOB_PAGE_SIZE = 250;

const RFM_MATRIX_COLUMNS: ListColumnCatalogItem[] = [
  { id: 'name', label: 'Name' },
  { id: 'email', label: 'Email', optional: true },
  { id: 'current_title', label: 'Current title' },
  { id: 'current_company', label: 'Company', optional: true },
  { id: 'location', label: 'Location', optional: true },
  { id: 'source', label: 'Source', optional: true },
  { id: 'skills', label: 'Skills', optional: true },
  { id: 'resume', label: 'Resume' },
  { id: 'fitment', label: 'Fitment' },
  { id: 'cov', label: 'Coverage (JD)' },
  { id: 'ats_score', label: 'ATS score' },
  { id: 'ats_rating', label: 'ATS rating', optional: true },
  { id: 'job', label: 'Job' },
  { id: 'client', label: 'Client' },
  { id: 'created_at', label: 'Created when', optional: true },
  { id: 'gaps', label: 'Skill gaps (#)', optional: true },
];

type RfmSortCol =
  | 'name'
  | 'email'
  | 'current_title'
  | 'current_company'
  | 'location'
  | 'source'
  | 'fitment'
  | 'cov'
  | 'ats_score'
  | 'ats_rating'
  | 'job'
  | 'client'
  | 'created_at'
  | 'gaps';

export function RoleFitPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState<Client[]>([]);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);

  const [clientFilter, setClientFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<JobStatus | ''>('open');
  const [candidateSearch, setCandidateSearch] = useState('');
  const [minFitment, setMinFitment] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [mainTab, setMainTab] = useState<'matrix' | 'bulk' | 'sourcing' | 'smart-match'>('matrix');
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const headerCheckRef = useRef<HTMLInputElement>(null);
  const [appliedKeys, setAppliedKeys] = useState<Set<string>>(new Set());
  const [applyingKey, setApplyingKey] = useState<string | null>(null);
  const [configureOpen, setConfigureOpen] = useState(false);
  const [manageRightsOpen, setManageRightsOpen] = useState(false);
  const [deleteCandidatesConfirm, setDeleteCandidatesConfirm] = useState<string[] | null>(null);
  const [rfmSortBy, setRfmSortBy] = useState<RfmSortCol>('created_at');
  const [rfmSortAsc, setRfmSortAsc] = useState(false);

  const rfmCatalog = useMemo(() => RFM_MATRIX_COLUMNS, []);
  const { show, setColumn, reset, visible } = useListColumnVisibility(
    'role_fit_matrix',
    user?.orgId ?? null,
    rfmCatalog,
  );

  const reloadPool = useCallback(() => {
    if (!user?.orgId) return;
    candidateService
      .list(user.orgId, { page: 1, pageSize: CANDIDATE_PAGE_SIZE })
      .then((cRes) => setCandidates((cRes as { data: Candidate[] }).data ?? []));
  }, [user?.orgId]);

  useEffect(() => {
    if (!user?.orgId) return;
    setLoading(true);
    Promise.all([
      clientService.list(user.orgId) as Promise<Client[]>,
      candidateService.list(user.orgId, { page: 1, pageSize: CANDIDATE_PAGE_SIZE }) as Promise<{ data: Candidate[] }>,
      jobService.list(user.orgId, { page: 1, pageSize: JOB_PAGE_SIZE }) as Promise<{ data: Job[] }>,
    ])
      .then(([cl, cRes, jRes]) => {
        setClients(Array.isArray(cl) ? cl : (cl as { data: Client[] }).data ?? []);
        setCandidates(cRes.data ?? []);
        setJobs(jRes.data ?? []);
      })
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  useEffect(() => {
    setPage(1);
    setSelectedKeys(new Set());
  }, [clientFilter, jobFilter, statusFilter, candidateSearch, minFitment]);

  const filteredJobs = useMemo(() => {
    let list = jobs;
    if (clientFilter) list = list.filter((j) => j.client_id === clientFilter);
    if (statusFilter) list = list.filter((j) => j.status === statusFilter);
    if (jobFilter) list = list.filter((j) => j.id === jobFilter);
    return list;
  }, [jobs, clientFilter, statusFilter, jobFilter]);

  const filteredCandidates = useMemo(() => {
    const q = candidateSearch.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(
      (c) =>
        (c.full_name ?? '').toLowerCase().includes(q) ||
        (c.email ?? '').toLowerCase().includes(q) ||
        (c.current_company ?? '').toLowerCase().includes(q),
    );
  }, [candidates, candidateSearch]);

  const pairCount = filteredCandidates.length * filteredJobs.length;

  const handleRfmSort = (col: RfmSortCol) => {
    if (rfmSortBy === col) {
      setRfmSortAsc((x) => !x);
    } else {
      setRfmSortBy(col);
      const numericFirstDesc = ['fitment', 'cov', 'ats_score', 'gaps'].includes(col);
      setRfmSortAsc(col === 'created_at' ? false : numericFirstDesc ? false : true);
    }
    setPage(1);
  };

  const rows: RoleFitRow[] = useMemo(() => {
    if (pairCount === 0 || pairCount > MAX_PAIRS) return [];
    const out: RoleFitRow[] = [];
    for (const c of filteredCandidates) {
      for (const j of filteredJobs) {
        out.push(computeRoleFitPair(c, j));
      }
    }
    const base = out.filter((r) => r.fitmentScore >= minFitment);
    const arr = [...base];
    const dir = rfmSortAsc ? 1 : -1;
    const str = (a: RoleFitRow, b: RoleFitRow, g: (r: RoleFitRow) => string) =>
      dir * g(a).localeCompare(g(b), undefined, { sensitivity: 'base' });
    const num = (a: RoleFitRow, b: RoleFitRow, g: (r: RoleFitRow) => number) => dir * (g(a) - g(b));
    arr.sort((a, b) => {
      switch (rfmSortBy) {
        case 'name':
          return str(a, b, (r) => r.candidate.full_name ?? '');
        case 'email':
          return str(a, b, (r) => r.candidate.email ?? '');
        case 'current_title':
          return str(a, b, (r) => r.candidate.current_title ?? '');
        case 'current_company':
          return str(a, b, (r) => r.candidate.current_company ?? '');
        case 'location':
          return str(a, b, (r) => r.candidate.location ?? '');
        case 'source':
          return str(a, b, (r) => r.candidate.source_channel ?? '');
        case 'fitment':
          return num(a, b, (r) => r.fitmentScore);
        case 'cov':
          return num(a, b, (r) => r.coverageScore);
        case 'ats_score':
          return num(a, b, (r) => r.atsOverall);
        case 'ats_rating':
          return str(a, b, (r) => r.atsRating);
        case 'job':
          return str(a, b, (r) => r.job.title ?? '');
        case 'client':
          return str(a, b, (r) => r.job.client?.name ?? '');
        case 'gaps':
          return num(a, b, (r) => r.gap.missingSkills.length);
        case 'created_at':
          return dir * (new Date(a.candidate.created_at).getTime() - new Date(b.candidate.created_at).getTime());
      }
    });
    return arr;
  }, [filteredCandidates, filteredJobs, minFitment, pairCount, rfmSortBy, rfmSortAsc]);

  const totalRowCount = rows.length;
  const totalPages = Math.max(1, Math.ceil(totalRowCount / pageSize));

  useEffect(() => {
    setPage((p) => Math.min(p, totalPages));
  }, [totalPages]);

  const effectivePage = Math.min(page, totalPages);

  const paginatedRows = useMemo(
    () => rows.slice((effectivePage - 1) * pageSize, effectivePage * pageSize),
    [rows, effectivePage, pageSize],
  );

  useEffect(() => {
    setSelectedKeys((prev) => {
      const allowed = new Set(paginatedRows.map((r) => r.key));
      const next = new Set<string>();
      prev.forEach((k) => {
        if (allowed.has(k)) next.add(k);
      });
      return next;
    });
  }, [paginatedRows]);

  useEffect(() => {
    if (headerCheckRef.current) {
      const n = paginatedRows.length;
      headerCheckRef.current.indeterminate = n > 0 && selectedKeys.size > 0 && selectedKeys.size < n;
    }
  }, [paginatedRows.length, selectedKeys.size]);

  const goOptimize = useCallback(
    (r: RoleFitRow) => {
      navigate('/single-optimize', {
        state: {
          fromRoleFit: {
            candidateName: r.candidate.full_name,
            candidateEmail: r.candidate.email ?? undefined,
            jobTitle: r.job.title,
            clientName: r.job.client?.name ?? undefined,
            candidateId: r.candidate.id,
            jobId: r.job.id,
            returnTo: '/role-fit',
          },
        },
      });
    },
    [navigate],
  );

  /** MAT-030: create an application from a matrix row and persist the fitment score */
  const applyToRole = useCallback(
    async (r: RoleFitRow) => {
      if (!user?.orgId || applyingKey) return;
      setApplyingKey(r.key);
      try {
        const fitmentScore = Math.round(r.fitmentScore);
        const fitmentJson: Record<string, unknown> = {
          matchedSkills: r.gap.matchedSkills,
          missingSkills: r.gap.missingSkills,
          impliedSkills: r.gap.impliedSkills ?? [],
          jdCoverage: r.coverageScore,
          atsScore: r.atsOverall,
        };
        await applicationService.create({
          org_id: user.orgId,
          candidate_id: r.candidate.id,
          job_id: r.job.id,
          client_id: r.job.client_id,
          fitment_score: fitmentScore,
          fitment_json: fitmentJson,
        });
        setAppliedKeys((prev) => new Set([...prev, r.key]));
      } catch {
        // silently ignore duplicate constraint errors
      } finally {
        setApplyingKey(null);
      }
    },
    [user?.orgId, applyingKey],
  );

  const exportCsv = useCallback((subset: RoleFitRow[]) => {
    const csv = buildRoleFitMatrixCsv(subset);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `role_fit_matrix_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  const selectedRowsOnPage = useMemo(
    () => paginatedRows.filter((r) => selectedKeys.has(r.key)),
    [paginatedRows, selectedKeys],
  );
  const toolbarSelectedCandidateIds = useMemo(
    () => selectedRowsOnPage.map((r) => r.candidate.id),
    [selectedRowsOnPage],
  );

  const handleMatrixOperationsExport = useCallback(
    (fmt: ListExportFmt, scope: 'selection' | 'all') => {
      if (fmt !== 'csv') {
        listExportNotImplemented('Role fit matrix', fmt, scope);
        return;
      }
      const subset =
        scope === 'all'
          ? rows
          : selectedKeys.size > 0
            ? rows.filter((r) => selectedKeys.has(r.key))
            : selectedRowsOnPage.length > 0
              ? selectedRowsOnPage
              : paginatedRows;
      exportCsv(subset);
    },
    [rows, selectedKeys, selectedRowsOnPage, paginatedRows, exportCsv],
  );

  const handleDeleteSelectedCandidates = useCallback(async (candidateIds: string[]) => {
    const unique = [...new Set(candidateIds)];
    if (unique.length === 0) return;
    setDeleteCandidatesConfirm(unique);
  }, []);

  if (!user?.orgId) return null;

  if (loading) return <FullPageSpinner />;

  const overflow = pairCount > MAX_PAIRS;

  const toggleKey = (key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllPage = () => {
    if (selectedKeys.size === paginatedRows.length && paginatedRows.length > 0) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(paginatedRows.map((r) => r.key)));
    }
  };

  return (
    <div className="w-full max-w-none min-w-0">
      <PageHeader
        breadcrumb={[{ label: 'Matching', href: '/role-fit' }, { label: 'Role fit matrix' }]}
        title="Role fit matrix"
        subtitle="Candidate × job fit from JD coverage and ATS-style scoring (same engine as Single optimize)."
        breadcrumbRowEnd={
          mainTab === 'matrix' ? (
            <ListOperationsDropdown
              objectHistoryHref={objectHistoryUrl('role_fit_matrix')}
              onExport={handleMatrixOperationsExport}
            />
          ) : undefined
        }
      />

      <div className="mb-5 rounded-xl border vscode-border vscode-bg-elevated shadow-sm">
        <div className="flex flex-wrap items-end gap-3 p-4 pb-3">
          <div className="flex min-w-0 flex-1 flex-wrap gap-2">
            <select
              className="input w-auto min-w-[160px]"
              value={clientFilter}
              onChange={(e) => {
                setClientFilter(e.target.value);
                setJobFilter('');
              }}
            >
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <select
              className="input w-auto min-w-[160px]"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as JobStatus | '')}
            >
              <option value="">All statuses</option>
              <option value="open">Open</option>
              <option value="on_hold">On hold</option>
              <option value="filled">Filled</option>
              <option value="closed">Closed</option>
            </select>
            <select className="input w-auto min-w-[200px]" value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
              <option value="">All jobs (filtered)</option>
              {filteredJobs.map((j) => (
                <option key={j.id} value={j.id}>
                  {j.title}
                  {j.client?.name ? ` — ${j.client.name}` : ''}
                </option>
              ))}
            </select>
            <div className="relative min-w-[min(100%,220px)] flex-1 basis-[200px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 vscode-text-tertiary" />
              <input
                className="input w-full pl-9"
                placeholder="Search candidates…"
                value={candidateSearch}
                onChange={(e) => setCandidateSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-3 border-t vscode-border px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <label className="flex min-w-0 flex-1 items-center gap-3 text-sm vscode-text-secondary">
            <span className="shrink-0 whitespace-nowrap">Min fitment</span>
            <input
              type="range"
              min={0}
              max={100}
              value={minFitment}
              onChange={(e) => setMinFitment(Number(e.target.value))}
              className="h-2 max-w-xs flex-1 cursor-pointer accent-[var(--vscode-primary)]"
            />
            <span className="w-10 shrink-0 tabular-nums text-sm font-medium vscode-text-primary">{minFitment}%</span>
          </label>
          <div className="flex flex-wrap items-center gap-2 text-xs sm:justify-end">
            <span className="inline-flex items-center rounded-md border vscode-border bg-[color-mix(in_srgb,var(--vscode-bg-secondary)_92%,transparent)] px-2.5 py-1 tabular-nums vscode-text-secondary">
              {filteredCandidates.length} candidates × {filteredJobs.length} jobs →{' '}
              <span className="ml-1 font-medium vscode-text-primary">{pairCount.toLocaleString()}</span> pairs
            </span>
            {pairCount > MAX_PAIRS ? (
              <span className="text-amber-400/90">Max {MAX_PAIRS.toLocaleString()} — narrow filters</span>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card overflow-hidden p-0">
        <div className="flex flex-wrap border-b vscode-border vscode-bg-tertiary/30">
          {(
            [
              { id: 'matrix' as const, label: 'Match matrix', icon: LayoutGrid },
              { id: 'bulk' as const, label: 'Bulk import (CSV)', icon: Upload },
              { id: 'sourcing' as const, label: 'Sourcing', icon: Search },
              { id: 'smart-match' as const, label: 'Smart match', icon: Sparkles },
            ] as const
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setMainTab(id)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
                mainTab === id
                  ? 'border-b-2 border-brand-500 text-brand-400 vscode-bg-primary/30'
                  : 'vscode-text-secondary hover:vscode-text-primary hover:vscode-bg-hover/50'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0 opacity-90" />
              {label}
            </button>
          ))}
        </div>

        {mainTab === 'bulk' && user && (
          <RoleFitBulkImportTab orgId={user.orgId} userId={user.id} onImported={reloadPool} />
        )}

        {mainTab === 'sourcing' && <RoleFitSourcingTab jobs={filteredJobs} />}

        {mainTab === 'smart-match' && (
          <div className="p-4">
            <SmartMatchRedirectCTA />
          </div>
        )}

        {mainTab === 'matrix' && overflow ? (
          <div className="p-6">
            <EmptyState
              icon={Target}
              title="Too many combinations"
              description={`You have ${pairCount.toLocaleString()} candidate–job pairs. Narrow client, job status, or pick a single job — maximum ${MAX_PAIRS.toLocaleString()} pairs per analysis.`}
            />
          </div>
        ) : mainTab === 'matrix' && filteredJobs.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={Target}
              title="No jobs match filters"
              description="Adjust client or status filters, or add jobs under your clients."
            />
          </div>
        ) : mainTab === 'matrix' && filteredCandidates.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={Target}
              title="No candidates"
              description="Add candidates to your pool, use Bulk import (CSV), or clear the search filter."
            />
          </div>
        ) : mainTab === 'matrix' && rows.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={Target}
              title="No rows above minimum fitment"
              description="Lower the min fitment slider or enrich candidate skills and job required skills for stronger signals."
            />
          </div>
        ) : mainTab === 'matrix' ? (
          <>
            <DataTableToolbar
              hasTable
              newLabel="New candidate"
              newHref="/candidates/new"
              selectedIds={toolbarSelectedCandidateIds}
              onEdit={(id) => navigate(`/candidates/${id}`)}
              onDelete={handleDeleteSelectedCandidates}
              deleteEntityLabel="candidates"
              onExport={() =>
                exportCsv(selectedRowsOnPage.length > 0 ? selectedRowsOnPage : paginatedRows)
              }
              onImport={() => setMainTab('bulk')}
              onManageRights={() => setManageRightsOpen(true)}
              toolbarEnd={
                <ListConfigureButton onClick={() => setConfigureOpen(true)} title="Role fit matrix table" />
              }
            />
            <div className="overflow-x-auto min-w-0">
              <table className="ggj-data-table min-w-[1100px]">
                <thead>
                  <tr className="border-b vscode-border text-left">
                    <th className="w-10">
                      <input
                        ref={headerCheckRef}
                        type="checkbox"
                        className="rounded border-gray-600"
                        checked={paginatedRows.length > 0 && selectedKeys.size === paginatedRows.length}
                        onChange={toggleAllPage}
                        aria-label="Select all on page"
                      />
                    </th>
                    <SortableTh label="Name" column="name" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('name'), '')} />
                    <SortableTh label="Email" column="email" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('email'), '')} />
                    <SortableTh label="Title" column="current_title" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('current_title'), '')} />
                    <SortableTh label="Company" column="current_company" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('current_company'), '')} />
                    <SortableTh label="Location" column="location" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('location'), '')} />
                    <SortableTh label="Source" column="source" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('source'), '')} />
                    <th className={colClass(show('skills'), 'px-4 py-3 uppercase text-xs tracking-wide font-semibold vscode-text-primary')}>Skills</th>
                    <th className={colClass(show('resume'), 'px-4 py-3 uppercase text-xs tracking-wide font-semibold vscode-text-primary')}>Resume</th>
                    <SortableTh label="Fitment" column="fitment" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('fitment'), '')} />
                    <SortableTh label="Cov." column="cov" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('cov'), '')} />
                    <SortableTh label="ATS" column="ats_score" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('ats_score'), '')} />
                    <SortableTh label="ATS rating" column="ats_rating" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('ats_rating'), '')} />
                    <SortableTh label="Job" column="job" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('job'), '')} />
                    <SortableTh label="Client" column="client" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('client'), '')} />
                    <SortableTh label="Created when" column="created_at" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('created_at'), 'whitespace-nowrap')} />
                    <SortableTh label="Gaps" column="gaps" current={rfmSortBy} asc={rfmSortAsc} onSort={handleRfmSort} className={colClass(show('gaps'), 'text-center w-14')} />
                    <th className="text-right w-36 px-4 py-3">[Actions]</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((r) => {
                    const pairTo = `/role-fit/${r.candidate.id}/${r.job.id}`;
                    return (
                      <tr
                        key={r.key}
                        className={`transition-colors ${
                          selectedKeys.has(r.key) ? 'bg-brand-500/10' : ''
                        }`}
                      >
                        <td className="w-10">
                          <input
                            type="checkbox"
                            className="rounded border-gray-600"
                            checked={selectedKeys.has(r.key)}
                            onChange={() => toggleKey(r.key)}
                            aria-label={`Select ${r.candidate.full_name}`}
                          />
                        </td>
                        <td className={colClass(show('name'), 'px-4 py-3')}>
                          <Link to={pairTo} className="group flex min-w-0 items-center gap-3 text-left">
                            <img
                              src={candidatePortraitUrl(r.candidate.id, 44)}
                              alt=""
                              className="h-11 w-11 shrink-0 rounded-lg border border-gray-700 object-cover"
                              width={44}
                              height={44}
                            />
                            <span className="font-medium vscode-text-primary group-hover:text-brand-400 transition-colors truncate">
                              {r.candidate.full_name}
                            </span>
                          </Link>
                        </td>
                        <td className={colClass(show('email'), 'px-4 py-3 text-xs vscode-text-secondary truncate max-w-[14rem]')}>
                          {r.candidate.email?.trim() || '—'}
                        </td>
                        <td className={colClass(show('current_title'), 'px-4 py-3 vscode-text-secondary')}>
                          {r.candidate.current_title?.trim() ? r.candidate.current_title : '—'}
                        </td>
                        <td className={colClass(show('current_company'), 'px-4 py-3 text-xs vscode-text-tertiary')}>
                          {r.candidate.current_company?.trim() || '—'}
                        </td>
                        <td className={colClass(show('location'), 'px-4 py-3 vscode-text-secondary')}>{r.candidate.location ?? '—'}</td>
                        <td className={colClass(show('source'), 'px-4 py-3 text-xs vscode-text-tertiary')}>
                          {r.candidate.source_channel ?? '—'}
                        </td>
                        <td className={colClass(show('skills'), 'px-4 py-3')}>
                          <div className="flex flex-wrap gap-1">
                            {(r.candidate.parsed_skills ?? []).slice(0, 3).map((s) => (
                              <span key={s} className="px-1.5 py-0.5 bg-gray-800 vscode-text-secondary text-xs rounded">
                                {s}
                              </span>
                            ))}
                            {(r.candidate.parsed_skills?.length ?? 0) > 3 && (
                              <span className="text-xs vscode-text-tertiary">+{(r.candidate.parsed_skills?.length ?? 0) - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className={colClass(show('resume'), 'px-4 py-3')}>
                          {r.candidate.base_resume_url ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-400">
                              <Upload className="w-3 h-3" /> Uploaded
                            </span>
                          ) : (
                            <span className="text-xs vscode-text-tertiary">None</span>
                          )}
                        </td>
                        <td className={colClass(show('fitment'), 'px-4 py-3')}>
                          <span className="font-semibold tabular-nums text-brand-400">{r.fitmentScore}</span>
                        </td>
                        <td className={colClass(show('cov'), 'px-4 py-3 tabular-nums vscode-text-secondary')}>{r.coverageScore}%</td>
                        <td className={colClass(show('ats_score'), 'px-4 py-3 tabular-nums vscode-text-secondary')}>{r.atsOverall}</td>
                        <td className={colClass(show('ats_rating'), 'px-4 py-3 text-xs vscode-text-tertiary')}>{r.atsRating}</td>
                        <td className={colClass(show('job'), 'px-4 py-3')}>
                          <Link to={`/jobs/${r.job.id}`} className="text-brand-400 hover:text-brand-300 text-xs line-clamp-2">
                            {r.job.title}
                          </Link>
                        </td>
                        <td className={colClass(show('client'), 'px-4 py-3')}>
                          <span className="line-clamp-2 text-xs vscode-text-secondary">{r.job.client?.name ?? '—'}</span>
                        </td>
                        <td className={colClass(show('created_at'), 'px-4 py-3 text-xs vscode-text-tertiary tabular-nums whitespace-nowrap')}>
                          {formatCreatedWhen(r.candidate.created_at)}
                        </td>
                        <td className={colClass(show('gaps'), 'px-4 py-3 text-center tabular-nums text-amber-300/90')}>{r.gap.missingSkills.length}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to={pairTo} className="text-xs text-brand-400 hover:text-brand-300 mr-2">
                            [View]
                          </Link>
                          <button
                            type="button"
                            className="text-xs text-brand-300 hover:text-brand-200 mr-2"
                            onClick={() => goOptimize(r)}
                          >
                            [Optimize]
                          </button>
                          <button
                            type="button"
                            className={`text-xs mr-2 ${
                              appliedKeys.has(r.key)
                                ? 'text-green-400 cursor-default'
                                : 'text-emerald-400 hover:text-emerald-300'
                            }`}
                            disabled={applyingKey === r.key || appliedKeys.has(r.key)}
                            onClick={() => applyToRole(r)}
                            title="Create application from this pair and persist fitment score"
                          >
                            {appliedKeys.has(r.key) ? '✓ Applied' : applyingKey === r.key ? '…' : '[Apply]'}
                          </button>
                          <Link to={pairTo} className="vscode-text-secondary hover:vscode-text-primary inline-flex align-middle">
                            <ChevronRight className="w-4 h-4" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        ) : null}

        {mainTab === 'matrix' && !overflow && filteredJobs.length > 0 && filteredCandidates.length > 0 && rows.length > 0 ? (
          <TablePagination
            page={effectivePage}
            pageSize={pageSize}
            totalItems={totalRowCount}
            onPageChange={(p) => {
              setPage(p);
            }}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
          />
        ) : null}
      </div>

      <ConfigureListColumnsModal
        open={configureOpen}
        onClose={() => setConfigureOpen(false)}
        entityLabel="Role fit matrix"
        columns={RFM_MATRIX_COLUMNS}
        value={visible}
        onChange={setColumn}
        onReset={reset}
      />
      <GenericManageRightsModal
        open={manageRightsOpen}
        onClose={() => setManageRightsOpen(false)}
        orgId={user?.orgId ?? null}
        entityKey="role_fit_matrix"
        subtitle="Per-user access for the Role fit matrix list — stored locally per browser."
      />
      <ConfirmDialog
        open={deleteCandidatesConfirm != null}
        onClose={() => setDeleteCandidatesConfirm(null)}
        onConfirm={() => {
          const unique = deleteCandidatesConfirm;
          setDeleteCandidatesConfirm(null);
          if (!unique?.length) return;
          void (async () => {
            for (const id of unique) {
              await candidateService.softDelete(id);
            }
            setCandidates((list) => list.filter((c) => !unique.includes(c.id)));
            setSelectedKeys(new Set());
          })();
        }}
        title="Remove from pool?"
        message={
          deleteCandidatesConfirm?.length
            ? `Remove ${deleteCandidatesConfirm.length} candidate${deleteCandidatesConfirm.length === 1 ? '' : 's'} from your pool? They will be hidden from lists and the matrix.`
            : ''
        }
        confirmLabel="Remove"
        variant="danger"
      />
    </div>
  );
}
